import { useEffect, useRef, useState } from 'react';

import { ROTATION_FREEZE_MS } from '../../../../constants';

/**
 * Whether a horizontal row is close enough to the lobby viewport to be worth filling.
 *
 * A row's own virtualizer only knows its horizontal scroll position, so a section sitting
 * ten screens away still renders a full window of cards - and sections are never unmounted
 * once built. That makes the mounted set grow with the number of sections rather than with
 * what is on screen, and each card holds a decoded thumbnail costing megabytes.
 *
 * Emptying a row has no layout consequences by construction: its scroller has a fixed
 * height from the card dimensions, and the inner spacer takes its width from the
 * virtualizer's total size, so neither the row's height nor its horizontal scroll range
 * depends on how many cards are rendered. Nothing moves, and scrolling back refills at the
 * same offsets.
 */

/**
 * Root heights of travel a row gets before it lets its cards go - and, in the other
 * direction, how far ahead it refills. The whole memory/latency dial.
 *
 * Split by direction because the two sides are not the same problem. A row above has been
 * on screen already, so its thumbnails are in the browser's cache and refilling it is a
 * decode; a row below may never have been seen, and refilling it can mean the network. So
 * the expensive side is the one that needs the lead time, and the cheap side is where a
 * wider margin is close to free - which is lucky, because scrolling back up is where a
 * late refill is most obvious: the user knows what was there.
 *
 * Both are safe to raise: a row that is actually visible intersects the root itself, so
 * widening either margin can never blank something on screen. The only cost is memory.
 */
const KEEP_CARDS_VIEWPORTS_ABOVE = 3;
const KEEP_CARDS_VIEWPORTS_BELOW = 1.5;

const SCROLLER_SELECTOR = '[data-section-container]';

/**
 * A rotation re-lays out every row while the scroller height and the margins built from it
 * lag behind (`--app-height` is debounced), so for a moment rows are reported near or far
 * against the wrong window - filling thumbnails that are dropped again, or dropping ones that
 * come straight back. For ROTATION_FREEZE_MS after the last flip a row therefore keeps its
 * cards, and an empty row fills only once it is about to come on screen; then every row takes
 * the margins' answer.
 */
// Root heights ahead of the screen edge at which an empty row still fills mid-rotation. Not 0:
// the fill lands a frame after the observer fires, so a row that waits until it is visible
// paints an empty sliver at the edge for that frame.
const ROTATION_FILL_LEAD_VIEWPORTS = 0.5;

let rotationTimer: number | undefined;
let portraitQuery: MediaQueryList | null = null;
const rotationEndListeners = new Set<() => void>();

function listenForRotation(): void {
  if (portraitQuery) return;
  portraitQuery = window.matchMedia('(orientation: portrait)');
  portraitQuery.addEventListener('change', () => {
    window.clearTimeout(rotationTimer);
    rotationTimer = window.setTimeout(() => {
      rotationTimer = undefined;
      rotationEndListeners.forEach((listener) => listener());
    }, ROTATION_FREEZE_MS);
  });
}

/**
 * Returns `true` - fill the row - until an enclosing lobby scroller says otherwise, so a
 * row rendered outside one (search results, campaign grids, Storybook) always fills.
 *
 * `resetKey` re-resolves the root and re-measures the margin; pass something that changes
 * with the viewport.
 */
export function useIsRowNearViewport(
  elementRef: React.RefObject<HTMLElement | null>,
  resetKey?: string | number,
): boolean {
  const [isNear, setIsNear] = useState(true);
  const isNearRef = useRef(true);

  useEffect(() => {
    const element = elementRef.current;
    const root = element?.closest(SCROLLER_SELECTOR);
    if (!element || !root) return undefined;

    listenForRotation();

    let observer: IntersectionObserver | null = null;
    let fillObserver: IntersectionObserver | null = null;
    let resolvedHeight = -1;
    let isInMargins: boolean | null = null;

    const setNear = (near: boolean) => {
      isNearRef.current = near;
      setIsNear(near);
    };
    const stopFillObserver = () => {
      fillObserver?.disconnect();
      fillObserver = null;
    };

    const onMarginsEntry = (isIntersecting: boolean) => {
      isInMargins = isIntersecting;
      if (rotationTimer === undefined) {
        setNear(isIntersecting);
        return;
      }
      if (!isIntersecting || isNearRef.current || fillObserver) return;
      const lead = Math.round(resolvedHeight * ROTATION_FILL_LEAD_VIEWPORTS);
      const rowRect = element.getBoundingClientRect();
      const rootRect = root.getBoundingClientRect();
      if (rowRect.bottom > rootRect.top - lead && rowRect.top < rootRect.bottom + lead) {
        setNear(true);
        return;
      }
      fillObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry?.isIntersecting) return;
          stopFillObserver();
          setNear(true);
        },
        { root, rootMargin: `${lead}px 0px ${lead}px 0px`, threshold: 0 },
      );
      fillObserver.observe(element);
    };

    const onRotationEnd = () => {
      stopFillObserver();
      if (isInMargins !== null) setNear(isInMargins);
    };
    rotationEndListeners.add(onRotationEnd);

    const observe = () => {
      // Resolved to pixels against the scroller rather than passed as a percentage, so the
      // window is identical on every engine. A scroller still waiting for layout would give
      // a zero margin - which reads as "keep only what is on screen" - so the viewport
      // stands in until it settles. A margin left stale by a rotation is harmless either
      // way: a visible row intersects the root itself and can never be reported as far.
      const rootHeight = root.clientHeight || window.innerHeight;
      if (rootHeight === resolvedHeight) return;
      resolvedHeight = rootHeight;
      observer?.disconnect();

      // top right bottom left - "above" is the top margin, since a row approaching from
      // above crosses the root's top edge.
      const rootMargin = [
        `${Math.round(rootHeight * KEEP_CARDS_VIEWPORTS_ABOVE)}px`,
        '0px',
        `${Math.round(rootHeight * KEEP_CARDS_VIEWPORTS_BELOW)}px`,
        '0px',
      ].join(' ');

      observer = new IntersectionObserver(([entry]) => onMarginsEntry(!!entry?.isIntersecting), {
        root,
        rootMargin,
        threshold: 0,
      });
      observer.observe(element);
    };

    // Margins follow the root's own height: on a rotation it changes before `resetKey` does
    // (stale margins would fill rows that are already far), and in portrait it can settle
    // only after `resetKey` has run (stale margins would leave rows below empty).
    // ResizeObserver runs ahead of the frame's intersection pass, so neither gets painted.
    const resizeObserver = new ResizeObserver(observe);
    observe();
    resizeObserver.observe(root);

    return () => {
      rotationEndListeners.delete(onRotationEnd);
      resizeObserver.disconnect();
      observer?.disconnect();
      stopFillObserver();
    };
  }, [elementRef, resetKey]);

  return isNear;
}
